import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/newHeader';
import TestCard from '../components/TestCard';
import axios from 'axios';

function KyTest() {
  const navigate = useNavigate();
  const [testData, setTestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTree, setSelectedTree] = useState('교육과정');
  const [treeNames, setTreeNames] = useState(['교육과정']);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollContainerRef = useRef(null);
  const [dragDistance, setDragDistance] = useState(0);

  useEffect(() => {
    const fetchTestData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/student/home/v3`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );

        if (response.data) {
          // v3 API 데이터 구조로 변환
          const stamps = response.data.data?.stamps || [];
          setTestData(stamps);
          const uniqueTreeNames = ['교육과정', ...new Set(stamps.map(item => item.tree_name).filter(Boolean))];
          setTreeNames(uniqueTreeNames);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/', { replace: true });
        }
      }
    };

    fetchTestData();
  }, [navigate]);

  // 드래그 스크롤 핸들러
  const handleMouseDown = useCallback((e) => {
    // SELECT, INPUT 등 폼 요소에서만 드래그 방지
    const formElements = ['SELECT', 'INPUT', 'TEXTAREA', 'BUTTON'];
    if (formElements.includes(e.target.tagName)) {
      return;
    }

    // 스크롤 가능한 요소 찾기
    const scrollElement = scrollContainerRef.current || document.documentElement;
    const currentScrollTop = scrollElement.scrollTop;

    setIsDragging(true);
    setStartY(e.clientY);
    setScrollTop(currentScrollTop);
    setDragDistance(0);
    document.body.style.cursor = 'grabbing';
    e.preventDefault(); // 텍스트 선택 방지
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();

    const y = e.clientY;
    const walk = (startY - y) * 1.5; // 스크롤 속도 조절 (1.5배속)
    const newScrollTop = scrollTop + walk;

    // 드래그 거리 추적
    setDragDistance(Math.abs(startY - y));

    // 스크롤 실행
    const scrollElement = scrollContainerRef.current || document.documentElement;
    scrollElement.scrollTop = newScrollTop;
  }, [isDragging, startY, scrollTop]);

  const handleMouseUp = useCallback(() => {
    document.body.style.cursor = 'grab';
    setIsDragging(false);
    // 클릭 판정을 위해 잠시 유지 후 리셋
    setTimeout(() => setDragDistance(0), 100);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      document.body.style.cursor = 'grab';
      setIsDragging(false);
    }
  }, [isDragging]);

  useEffect(() => {
    // 페이지 로드 시 body에 grab 커서 설정
    document.body.style.cursor = 'grab';

    // 이벤트 리스너 등록
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = 'auto';
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp]); // 콜백 함수들을 의존성으로

  const filteredTestData = testData?.filter(item =>
    selectedTree === '교육과정' || item.tree_name === selectedTree
  );

  // 모든 exam을 하나의 배열로 만들어서 날짜순으로 정렬
  const allExams = [];
  filteredTestData?.forEach(stamp => {
    stamp.exams?.forEach((exam, examIndex) => {
      allExams.push({
        stamp,
        exam,
        examIndex
      });
    });
  });
  
  // 날짜 기준 내림차순 정렬
  allExams.sort((a, b) => {
    const dateA = new Date(a.exam.updated_at || a.exam.start_time || 0);
    const dateB = new Date(b.exam.updated_at || b.exam.start_time || 0);
    return dateB - dateA;
  });

  if (isLoading) return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-700 font-medium animate-pulse">로딩중...</p>
          </div>
        </main>
      </div>
    </div>
  );

  return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />

      <div
        ref={scrollContainerRef}
        className="relative z-20 flex flex-col min-h-screen"
        style={{ overflowY: 'auto', height: '100vh', cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <Header />
        <main
          className={`flex-grow flex flex-col items-center pt-[84px] pb-[120px] px-4 hide-scrollbar ${isDragging ? 'select-none cursor-grabbing' : 'cursor-grab'}`}
          style={{
            userSelect: isDragging ? 'none' : 'auto',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}>
          <div className="max-w-[700px] w-full mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-[18px] font-bold text-[#231F20]">테스트 목록</h1>
              <select
                value={selectedTree}
                onChange={(e) => setSelectedTree(e.target.value)}
                className={`w-[120px] h-[28px] px-3 rounded-3xl border border-gray-200 text-[14px] font-medium truncate text-gray-500 bg-white appearance-none cursor-pointer`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center'
                }}
              >
                {treeNames.map((treeName) => (
                  <option 
                    key={treeName} 
                    value={treeName}
                    className={treeName === selectedTree ? 'text-[#231F20]' : 'text-[#A3A1A2]'}
                  >
                    {treeName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className={`space-y-3 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                 style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              {allExams.map(({ stamp, exam, examIndex }) => {
                // 정답/오답 계산
                let correctCount = 0;
                let totalQuestions = 0;
                
                if (exam.by_step) {
                  for (let level = 1; level <= 5; level++) {
                    const stepData = exam.by_step[String(level)];
                    if (Array.isArray(stepData) && stepData.length >= 2) {
                      const [totalProblems, correct] = stepData;
                      totalQuestions += totalProblems || 0;
                      correctCount += correct || 0;
                    }
                  }
                } else if (exam.length) {
                  totalQuestions = exam.length;
                }
                
                const wrongCount = totalQuestions > 0 ? totalQuestions - correctCount : 0;
                
                const testCardData = {
                  ...exam,
                  tree_name: stamp.tree_name,
                  edu_ver: stamp.edu_ver || '고등학교 1학년',
                  average_difficulty: exam.average_difficulty || (exam.difficulty ? exam.difficulty / 100 : undefined),
                  problem_count: totalQuestions || exam.length || 25,
                  correct_count: correctCount,
                  wrong_count: wrongCount,
                  exam_count: exam.exam_count || examIndex + 1
                };
                
                return (
                  <TestCard
                    key={exam.exam_id}
                    testData={testCardData}
                    index={examIndex}
                    isClickable={true}
                    isReportReady={exam.report_info?.is_closed === true}
                    onClick={() => {
                      // 드래그가 5픽셀 이하일 때만 클릭으로 간주
                      if (dragDistance < 5) {
                        navigate(`/myresult/${exam.exam_id}`);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default KyTest;
